import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Inbox, Dog, CheckCircle, XCircle, ArrowLeft, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import CareRequestsProvider from '@/components/CareRequestsProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getPhotoUrl = (path) => !path ? null : (path.startsWith('http') ? path : `${API_URL}${path}`);

const ProviderRequests = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'published' || searchParams.get('request')
      ? 'published-requests'
      : 'contact-requests'
  );
  const [hasSubscription, setHasSubscription] = useState(false);
  const [contactRequests, setContactRequests] = useState([]);
  const [publishedCount, setPublishedCount] = useState(0);
  const [respondingTo, setRespondingTo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, subRes] = await Promise.all([
        api.get('/providers/my-profile'),
        api.get('/subscription/my').catch(() => ({ data: { has_subscription: false } }))
      ]);

      setProvider(profileRes.data);
      setHasSubscription(subRes.data.has_subscription || subRes.data.status === 'active');

      try {
        const crRes = await api.get('/contact-requests/received');
        setContactRequests(crRes.data);
      } catch {}

      try {
        const careReqRes = await api.get('/care-requests');
        setPublishedCount(careReqRes.data.length);
      } catch {}

    } catch {
      navigate('/provider/register');
    } finally {
      setLoading(false);
    }
  };

  const pendingDirect = contactRequests.filter(r => r.status === 'pending').length;
  const totalOpportunities = pendingDirect + publishedCount;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-6">No tienes perfil de cuidador.</p>
          <Link to="/provider/register">
            <Button className="bg-[#E6202E] hover:bg-[#D31522]">Crear Perfil</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="provider-requests-page">
      <div className="max-w-5xl mx-auto px-4 py-8">

        <div className="mb-6">
          <Link
            to="/provider/dashboard"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#E6202E] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al panel
          </Link>

          <div className="bg-white rounded-3xl shadow-sm border p-6 md:p-8 overflow-hidden relative">
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#E6202E] text-white px-3 py-1 rounded-full text-sm font-semibold mb-3">
                  <Sparkles className="w-4 h-4" />
                  {totalOpportunities} oportunidad{totalOpportunities === 1 ? '' : 'es'} para ti
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Solicitudes</h1>
                <p className="text-gray-500 mt-2 max-w-2xl">
                  Revisa clientes que quieren contactarte directamente y solicitudes publicadas disponibles para enviar una propuesta.
                </p>
              </div>

              {pendingDirect > 0 && (
                <button
                  onClick={() => setActiveTab('contact-requests')}
                  className="inline-flex items-center justify-center gap-2 bg-[#E6202E] hover:bg-[#D31522] text-white px-5 py-3 rounded-2xl text-sm font-bold shadow-sm transition-colors"
                >
                  <Inbox className="w-4 h-4" />
                  Revisar {pendingDirect} pendiente{pendingDirect === 1 ? '' : 's'}
                </button>
              )}
            </div>
          </div>
        </div>

        {pendingDirect > 0 && (
          <div className="mb-6 bg-red-50 border border-red-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                <Inbox className="w-5 h-5 text-[#E6202E]" />
              </div>
              <div>
                <p className="font-bold text-gray-900">
                  Tienes {pendingDirect} solicitud{pendingDirect === 1 ? '' : 'es'} directa{pendingDirect === 1 ? '' : 's'} esperando respuesta.
                </p>
                <p className="text-sm text-gray-500">Mientras antes respondas, más rápido puedes desbloquear el chat.</p>
              </div>
            </div>

            <Button
              onClick={() => setActiveTab('contact-requests')}
              className="bg-[#E6202E] hover:bg-[#D31522] text-white"
            >
              Revisar ahora
            </Button>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => setActiveTab('contact-requests')}
            className={`text-left bg-white rounded-3xl border p-6 shadow-sm transition-all hover:-translate-y-0.5 ${
              activeTab === 'contact-requests'
                ? 'border-[#E6202E] ring-4 ring-[#E6202E]/10 bg-red-50/40'
                : 'border-gray-100 hover:border-gray-200'
            }`}
            data-testid="requests-tab-direct"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#E6202E] flex items-center justify-center">
                <Inbox className="w-6 h-6 text-white" />
              </div>
              {pendingDirect > 0 && (
                <span className="bg-[#E6202E] text-white text-xs font-bold px-3 py-1 rounded-full">
                  Pendientes
                </span>
              )}
            </div>

            <div className="mt-5">
              <p className="text-5xl font-black text-gray-900 leading-none">{contactRequests.length}</p>
              <h2 className="font-bold text-lg mt-3">Contactos directos</h2>
              <p className="text-sm text-gray-500 mt-1">
                Clientes que quieren hablar contigo directamente.
              </p>
            </div>
          </button>

          <button
            onClick={() => setActiveTab('published-requests')}
            className={`text-left bg-white rounded-3xl border p-6 shadow-sm transition-all hover:-translate-y-0.5 ${
              activeTab === 'published-requests'
                ? 'border-[#E6202E] ring-4 ring-[#E6202E]/10 bg-red-50/40'
                : 'border-gray-100 hover:border-gray-200'
            }`}
            data-testid="requests-tab-published"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#E6202E] flex items-center justify-center">
                <Dog className="w-6 h-6 text-white" />
              </div>
              <span className="bg-gray-100 text-gray-600 text-xs font-bold px-3 py-1 rounded-full">
                Disponibles
              </span>
            </div>

            <div className="mt-5">
              <p className="text-5xl font-black text-gray-900 leading-none">{publishedCount}</p>
              <h2 className="font-bold text-lg mt-3">Solicitudes abiertas</h2>
              <p className="text-sm text-gray-500 mt-1">
                Clientes buscando cuidador donde puedes enviar propuesta.
              </p>
            </div>
          </button>
        </div>

        {activeTab === 'contact-requests' && (
          <div className="bg-white rounded-3xl p-6 shadow-sm border" data-testid="contact-requests-section">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
              <Inbox className="w-5 h-5 text-[#E6202E]" />
              Solicitudes de Contacto Directas
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Clientes premium que quieren contactarte. Si aceptas, se desbloquea el chat.
            </p>

            {contactRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Inbox className="w-14 h-14 mx-auto mb-3 opacity-50" />
                <p>No tienes solicitudes de contacto</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contactRequests.map(req => (
                  <div
                    key={req.request_id}
                    className={`p-4 rounded-2xl border ${
                      req.status === 'accepted'
                        ? 'bg-green-50 border-green-200'
                        : req.status === 'rejected'
                          ? 'bg-gray-50 border-gray-200 opacity-70'
                          : 'bg-white border-blue-200'
                    }`}
                    data-testid={`contact-req-${req.request_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                        {req.client_picture ? (
                          <img src={req.client_picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-500 font-bold text-lg">
                            {(req.client_name || 'C')[0]}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{req.client_name}</p>
                        <p className="text-sm text-gray-600 mt-1">{req.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(req.created_at).toLocaleDateString('es-CL')}
                        </p>

                        {req.pet && (
                          <div className="mt-3 flex items-center gap-3 p-3 bg-gray-50 rounded-xl border">
                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-red-50 flex items-center justify-center shrink-0">
                              {req.pet.photo ? (
                                <img src={getPhotoUrl(req.pet.photo)} alt={req.pet.name || 'Mascota'} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[#E6202E] font-bold text-xl">🐶</span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-gray-900">{req.pet.name || 'Mascota'}</p>
                              <p className="text-xs text-gray-600">
                                {[req.pet.breed, req.pet.species, req.pet.age ? `${req.pet.age} año${Number(req.pet.age) === 1 ? '' : 's'}` : '', req.pet.size, req.pet.gender]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {req.status === 'pending' && (
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={async () => {
                            setRespondingTo(req.request_id);
                            try {
                              await api.put(`/contact-requests/${req.request_id}/accept`);
                              toast.success('Solicitud aceptada. Chat desbloqueado.');
                              setContactRequests(prev => prev.map(r =>
                                r.request_id === req.request_id ? { ...r, status: 'accepted' } : r
                              ));
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Error');
                            } finally {
                              setRespondingTo(null);
                            }
                          }}
                          disabled={respondingTo === req.request_id}
                          className="bg-green-600 hover:bg-green-700 text-white flex-1"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Aceptar
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            setRespondingTo(req.request_id);
                            try {
                              await api.put(`/contact-requests/${req.request_id}/reject`);
                              toast.info('Solicitud rechazada.');
                              setContactRequests(prev => prev.map(r =>
                                r.request_id === req.request_id ? { ...r, status: 'rejected' } : r
                              ));
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Error');
                            } finally {
                              setRespondingTo(null);
                            }
                          }}
                          disabled={respondingTo === req.request_id}
                          className="flex-1"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Rechazar
                        </Button>
                      </div>
                    )}

                    {req.status === 'accepted' && (
                      <div className="mt-3 pt-3 border-t border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-green-800 font-bold">
                            Contacto aceptado · Servicio en curso
                          </span>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <Link to="/historial?tab=en-curso" className="flex-1">
                            <Button
                              size="sm"
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Ver servicio
                            </Button>
                          </Link>

                          <Link to="/chat" className="flex-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full bg-white"
                            >
                              <MessageCircle className="w-4 h-4 mr-1" />
                              Abrir chat
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}

                    {req.status === 'rejected' && (
                      <div className="mt-3 flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500">Rechazada</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'published-requests' && (
          <CareRequestsProvider hasSubscription={hasSubscription} onCountChange={setPublishedCount} />
        )}
      </div>
    </div>
  );
};

export default ProviderRequests;
